/**
 * outboundSend — thin wrappers over the two external-delivery callables used
 * by the compose-bar channel selector (M3.4 B1).
 *
 *   sendWhatsApp   → waSendManualReply (existing CF, dual-number routing)
 *   sendEmail      → sendEmailToClient (new M3.4 CF, project-keyed Reply-To)
 *
 * Internal notes do NOT go through here — they use postMessage() directly
 * (the AD-005 internal-by-default path). These wrappers exist only for the
 * two deliberate "Send to client" channels.
 */

/** The WA workspace conversation key for a project's default thread. */
export function projectConversationKey(projectId: string): string {
  return `proj:${projectId}:default`;
}

async function callable<TReq, TRes>(name: string, data: TReq): Promise<TRes> {
  const [{ functions }, { httpsCallable }] = await Promise.all([
    import('./firebase'),
    import('firebase/functions'),
  ]);
  const fn = httpsCallable<TReq, TRes>(functions, name);
  const res = await fn(data);
  return res.data;
}

/**
 * Send a free-form WhatsApp reply into a project conversation via the
 * existing waSendManualReply callable. Throws (HttpsError) on failure —
 * e.g. no phone on record, or the 24h window is closed.
 */
export async function sendWhatsApp(params: {
  projectId: string;
  text: string;
}): Promise<{ ok: boolean; waMessageId?: string | null }> {
  return callable('waSendManualReply', {
    conversationKey: projectConversationKey(params.projectId),
    text: params.text,
  });
}

/**
 * Send an email to the project's client via the M3.4 sendEmailToClient
 * callable. Throws (HttpsError) on failure — e.g. failed-precondition when
 * the contact has no email on file.
 */
export async function sendEmail(params: {
  projectId: string;
  contactId: string;
  body: string;
  subject?: string;
}): Promise<{ ok: boolean; messageId?: string | null }> {
  return callable('sendEmailToClient', params);
}
